// landingView — what the "How it lands in …" panel shows for the page that
// is open, projected from the server's answer (`Packaging.Landing`). Pure.
//
// Only the blocks written on the open page count (a fate names the material
// it is written in). Each block that changes shape or is not sent gets a row
// (blocks with the same fate and the same name share one); every block that
// arrives as it is joins one row at the end. A part of the open page that
// has no fate at all (planned, empty or removed: export carries nothing of
// it) is a row of its own, so nothing on the page goes unmentioned. The same
// answer gives the margin marks (per block id, per part id).

export type {
    LandingAnswer,
    LandingMark,
    LandingRow,
    LandingRowHow,
    LandingRowWhat,
    LandingView,
};
export { landingViewOf, proposalsFor };

import { exhaustSwitchCase } from "@bilda/utils";
import type {
    BlockFate,
    Landing,
    LandingFate,
    MoveTextProposal,
} from "../../app/ports/landing.js";

// The server's answer as the panel holds it.
type LandingAnswer =
    | { readonly type: "waiting" }
    | { readonly type: "failed" }
    | { readonly type: "landed"; readonly landing: Landing };

// The margin mark of a block while the panel is open.
type LandingMark = "reshaped" | "not_sent";

type LandingRowWhat =
    | { readonly type: "text_at_top" }
    | { readonly type: "text_between" }
    | { readonly type: "text_at_end" }
    | { readonly type: "part"; readonly title: string }
    | { readonly type: "kind"; readonly kind: string }
    // Everything that arrives as it is; `alone`: nothing else to say.
    | { readonly type: "the_rest"; readonly alone: boolean };

type LandingRowHow =
    | { readonly type: "fate"; readonly fate: LandingFate }
    // A part export carries nothing of (planned, empty or removed).
    | { readonly type: "nothing_of_it" };

type LandingRow = {
    readonly key: string;
    readonly what: LandingRowWhat;
    readonly how: LandingRowHow;
    // The blocks (ids on the page) and parts (resource ids) it speaks of.
    readonly blocks: readonly string[];
    readonly parts: readonly string[];
};

type LandingView = {
    readonly rows: readonly LandingRow[];
    readonly blocks: ReadonlyMap<string, LandingMark>;
    readonly parts: ReadonlyMap<string, LandingMark>;
};

const markOf = (fate: LandingFate): LandingMark | undefined => {
    switch (fate.type) {
        case "kept":
            return undefined;
        case "reshaped":
            return "reshaped";
        case "not_sent":
            return "not_sent";
        default:
            return exhaustSwitchCase(fate);
    }
};

const whatOf = (
    f: BlockFate,
    titles: ReadonlyMap<string, string>,
): LandingRowWhat => {
    if (f.part !== undefined)
        return { type: "part", title: titles.get(f.part) ?? f.kind };
    if (f.fate.type === "reshaped" && f.fate.into.type === "page_of_its_own") {
        const place = f.fate.into.place;
        switch (place.type) {
            case "first":
                return { type: "text_at_top" };
            case "before":
                return { type: "text_between" };
            case "after":
                return { type: "text_at_end" };
            default:
                return exhaustSwitchCase(place);
        }
    }
    return { type: "kind", kind: f.kind };
};

const landingViewOf = (
    landing: Landing,
    // The page that is open (a course, module or document id).
    page: string,
    // The open page's parts, in order (none: a document).
    parts: readonly string[],
    // Part titles by id, for the rows that name one.
    titles: ReadonlyMap<string, string>,
): LandingView => {
    const here = landing.fates.filter((f) => f.owner === page);
    const odd = here.filter((f) => f.fate.type !== "kept");
    // One row per (what, how): text between parts in several blocks is one.
    const grouped = odd.reduce<ReadonlyMap<string, LandingRow>>((rows, f) => {
        const what = whatOf(f, titles);
        const how: LandingRowHow = { type: "fate", fate: f.fate };
        const key = JSON.stringify([what, how]);
        const row = rows.get(key);
        return new Map(rows).set(key, {
            key,
            what,
            how,
            blocks: [...(row?.blocks ?? []), f.node],
            parts: [
                ...(row?.parts ?? []),
                ...(f.part === undefined ? [] : [f.part]),
            ],
        });
    }, new Map());
    const landed = new Set(
        here.flatMap((f) => (f.part === undefined ? [] : [f.part])),
    );
    const missing: readonly LandingRow[] = parts
        .filter((p) => !landed.has(p))
        .map((p) => ({
            key: `missing:${p}`,
            what: { type: "part", title: titles.get(p) ?? p },
            how: { type: "nothing_of_it" },
            blocks: [],
            parts: [p],
        }));
    const kept = here.filter((f) => f.fate.type === "kept");
    const others = [...grouped.values(), ...missing];
    const rest: readonly LandingRow[] =
        kept.length === 0
            ? []
            : [
                  {
                      key: "the_rest",
                      what: { type: "the_rest", alone: others.length === 0 },
                      how: { type: "fate", fate: { type: "kept" } },
                      blocks: kept.map((f) => f.node),
                      parts: kept.flatMap((f) =>
                          f.part === undefined ? [] : [f.part],
                      ),
                  },
              ];
    const marked = here.flatMap(
        (f): readonly (readonly [BlockFate, LandingMark])[] => {
            const m = markOf(f.fate);
            return m === undefined ? [] : [[f, m]];
        },
    );
    return {
        rows: [...others, ...rest],
        blocks: new Map(marked.map(([f, m]) => [f.node, m])),
        parts: new Map([
            ...marked.flatMap(
                ([f, m]): readonly (readonly [string, LandingMark])[] =>
                    f.part === undefined ? [] : [[f.part, m]],
            ),
            ...missing.flatMap(
                (r): readonly (readonly [string, LandingMark])[] =>
                    r.parts.map((p) => [p, "not_sent"]),
            ),
        ]),
    };
};

// The proposals that concern the open page: its own text, or the page the
// text would move to.
const proposalsFor = (
    landing: Landing,
    page: string,
): readonly MoveTextProposal[] =>
    landing.proposals.filter((p) => p.container === page || p.part === page);
